import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'

import { useLeaderboard, useStatus, useWorkshops } from '../context/atomContext'
import { SocialContext } from '../context/socialContext'

import {
  checkIsMe,
  formattedScore,
  divisionForID,
  levelForID,
  getMessageFromError,
} from '../Helpers'

import AppLayout from '../components/layout/Layout'

import DayPicker from '../components/elements/DayPicker'
import Button from '../components/elements/Button'
import Zipper from '../components/elements/Zipper'

import Spinner from '../components/elements/Spinner'
import SelectKit from '../components/molecules/SelectKit'
import FilterByDivision from '../components/molecules/FilterByDivision'
import FilterByLevel from '../components/molecules/FilterByLevel'

import LeaderboardRow from '../components/structures/LeaderboardRow'

function WorkshopLeaderboard() {
  const track = 'workshop'
  const { workshopID } = useParams()
  const history = useHistory()

  const { data: workshops, isLoading: isWorkshopsLoading } = useWorkshops()

  const workshop =
    !isWorkshopsLoading &&
    workshops?.value?.rows?.find(ws => ws.slug === workshopID)

  const lastFiltersData = localStorage.getItem(
    `filtersForTrack-${track}-${workshopID}`,
  )
  let lastFilters
  try {
    lastFilters = JSON.parse(lastFiltersData)
  } catch {
    console.log('Failed to parse previous filters')
  }

  const lastKitFilter = lastFilters?.kit ? lastFilters.kit : undefined
  const lastLevelFilter =
    lastFilters?.level && lastFilters?.level !== '0'
      ? levelForID(lastFilters.level)
      : {}
  const lastDivisionFilter =
    lastFilters?.division && lastFilters?.division !== '0'
      ? divisionForID(lastFilters.division)
      : {}

  const queryParams = new URLSearchParams(window.location.search)
  const [date, setDate] = useState(queryParams.get('day') || 1)
  const [activeDivision, setActiveDivision] = useState(lastDivisionFilter)
  const [activeLevel, setActiveLevel] = useState(lastLevelFilter)
  const [popComment, setPopComment] = useState(null)

  const { toggleLike, openComments, openProfile } = useContext(SocialContext)

  const defaultKit = workshop?.kits?.[0]?.id || 'POWER_KIT'

  const [activeKitFilter, setActiveKitFilter] = useState(
    queryParams.get('kit') || lastKitFilter || defaultKit,
  )

  const saveFilters = useCallback(
    val => {
      localStorage.setItem(
        `filtersForTrack-${track}-${workshopID}`,
        JSON.stringify(val),
      )
    },
    [workshopID],
  )

  const selectKitFilter = useCallback(
    item => {
      const kitID = Object.keys(item).includes('id') ? item?.id : item
      const currentUrlParams = new URLSearchParams(window.location.search)

      currentUrlParams.set('kit', kitID)

      history.replace(
        `${history.location.pathname}?${currentUrlParams.toString()}`,
      )

      setActiveKitFilter(kitID)
      saveFilters({
        kit: kitID,
        division: activeDivision?.id,
        level: activeLevel?.id,
      })
    },
    [activeDivision, activeLevel, history, saveFilters],
  )

  const selectDivision = item => {
    setActiveDivision(item)
    saveFilters({
      kit: activeKitFilter,
      division: item?.id,
      level: activeLevel?.id,
    })
  }
  const selectLevelFilter = item => {
    setActiveLevel(item)
    saveFilters({
      kit: activeKitFilter,
      division: activeDivision?.id,
      level: item?.id,
    })
  }

  const {
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    error,
    isError: isLeaderboardError,
    data,
    refetch,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
  } = useLeaderboard(
    undefined,
    {
      kit: activeKitFilter,
      division: activeDivision.id,
      level: activeLevel.id,
      workshopId: workshop?.id,
      day: date,
    },
    false,
    track,
  )

  const trainingDates = () =>
    workshop?.user?.completed.map(elem => parseInt(elem, 10)) || []

  const hasData =
    data?.pages?.length > 0 && data.pages[0].value?.rows?.length > 0

  const workoutData = data?.pages?.length && data.pages[0].value?.workout

  const workoutKits = workoutData
    ? Object.keys(workoutData).filter(k => k !== 'format')
    : []

  const status = useStatus()
  const location = useLocation()
  const returnTo = location?.state?.returnTo
  const commentId = location?.state?.commentId

  useEffect(() => {
    const kits = data?.pages?.length
      ? Object.keys(workoutData).filter(k => k !== 'format')
      : []

    if (
      activeKitFilter &&
      (kits.length === 0 || kits.includes(activeKitFilter))
    ) {
      return
    }
    if (kits.length === 0 || kits.includes(defaultKit)) {
      selectKitFilter(defaultKit)
    }
    selectKitFilter(kits[0])
  }, [activeKitFilter, data, defaultKit, selectKitFilter, track, workoutData])

  useEffect(() => {
    if (commentId && popComment !== commentId) {
      openComments({ id: commentId })
      setPopComment(commentId)
    }
  }, [commentId, popComment, openComments])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const aDate = params.get('day') || 1

    if (date !== aDate) {
      setDate(aDate)
    }
  }, [date, location])

  const getPageNumber = page => {
    if (!page?.value?.pagination?.self) return undefined
    const myurl = new URL(page.value.pagination.self)
    return myurl.searchParams.get('page')
  }

  const getMyRow = () => data?.pages?.length > 0 && data?.pages[0]?.value?.user

  const myRow = getMyRow()

  const updateDate = aDate => {
    const currentUrlParams = new URLSearchParams(window.location.search)

    currentUrlParams.set('day', aDate)
    if (aDate !== date) {
      history.replace(
        `${history.location.pathname}?${currentUrlParams.toString()}`,
      )

      setDate(aDate)
    }
  }

  const showUserRow = () => {
    if (!myRow || myRow.length === 0 || !myRow.row) return false
    const myPageUrl = new URL(myRow.page)
    const myPageNum = myPageUrl.searchParams.get('page')
    let returnVal = true
    data.pageParams.forEach((page, index) => {
      if (Number(myPageNum) === Number(page)) returnVal = false
      if (
        page === undefined &&
        Number(myPageNum) === Number(data.pages[index].value.filters.page)
      )
        returnVal = false
    })
    return returnVal
  }

  const isAltActionActive =
    !Object.is(activeKitFilter, defaultKit) ||
    Object.keys(activeLevel).length > 0 ||
    Object.keys(activeDivision).length > 0

  const headerConfig = {
    backAction: () => {
      if (returnTo) history.push(returnTo)
      else history.push('/')
    },
    label: 'Leaderboard',
    altAction: {
      onClick: () => {
        selectKitFilter(defaultKit)
        setActiveLevel({})
        setActiveDivision({})
      },
      icon: { name: 'reset-filters', type: 'svg', size: 'lg' },
      label: 'Filters',
      isActive: isAltActionActive,
    },
  }

  const filters = (
    <nav
      className="rpm-filters leaderboard--filters"
      id="leaderboardFilters"
      key="filters"
    >
      <DayPicker
        days={parseInt(workshop?.sessions, 10) || 1}
        mode="day"
        selectedDay={date || 1}
        eventDates={trainingDates()}
        handleDate={updateDate}
        config={{
          tools: ['previous', 'next'],
          maxDate: parseInt(workshop?.sessions, 10) || 1,
          minDate: 1,
        }}
      />

      <SelectKit
        item={activeKitFilter}
        allowedKits={workoutKits}
        doSelect={selectKitFilter}
        track={track}
        isDisabled={false}
        kits={workshop?.kits}
      />
      <FilterByLevel item={activeLevel} doSelect={selectLevelFilter} />
      <FilterByDivision item={activeDivision} doSelect={selectDivision} />
      <Zipper isActive={isFetching} />
    </nav>
  )

  const subheader = hasData ? (
    <div
      id="leaderboardSubheader"
      className="rpm-subheader leaderboard--subheader"
      key="subheader"
    >
      <span>
        Rank{' '}
        {data?.pages?.length > 0 && data?.pages[0].value?.totalCount && (
          <b>
            <em>/{data.pages[0].value.totalCount}</em>
          </b>
        )}
      </span>
      <span>Result</span>
    </div>
  ) : null

  return (
    <AppLayout
      name="leaderboard"
      headerConfig={headerConfig}
      filters={filters}
      subheader={subheader}
      isLoading={
        isLoading ||
        (isFetching && !isFetchingNextPage && !isFetchingPreviousPage)
      }
      isError={isLeaderboardError}
      errorMessage={getMessageFromError(error)}
      errorAction={() => window.location.reload()}
      cnames="one-column-content"
    >
      <section className="app-content leaderboard--content">
        {((!isLoading && !hasData) || error) && (
          <div className="app-content--inner rpm-description rpm-description--fallback">
            <p>No Leaderboard for this day.</p>
          </div>
        )}

        {hasData && (
          <>
            {hasPreviousPage && !isFetchingPreviousPage && (
              <Button
                label="Load Previous"
                onClick={() => fetchPreviousPage()}
              />
            )}

            {isFetchingPreviousPage && (
              <div className="leaderboard--infinite-spinner">
                <Spinner size={3} />
              </div>
            )}

            <ul className="app-content--inner rpm-data-list leaderboard--results">
              {data?.pages &&
                data.pages.map(
                  page =>
                    page.value.rows &&
                    page.value.rows.map(row => (
                      <LeaderboardRow
                        isMe={checkIsMe(status, row)}
                        showRank={checkIsMe(status, row)}
                        row={row}
                        results={page.value.totalCount}
                        comments={row.commentCount.toString()}
                        likes={row.likeCount.toString()}
                        toggleLike={() =>
                          toggleLike(row.id, track, {
                            onSuccess: () =>
                              refetch({
                                refetchPage: p =>
                                  getPageNumber(page) === getPageNumber(p),
                              }),
                          })
                        }
                        score={
                          row.beatCap
                            ? formattedScore(row.score, page.value.scoring)
                            : row.tiebreak
                        }
                        key={`${row.name}-${row.rank}`}
                        openComments={() =>
                          openComments({
                            ...row,
                            track,
                            training: { workout: workoutData },
                            onCommentSuccess: () => {
                              refetch({
                                refetchPage: p =>
                                  getPageNumber(page) === getPageNumber(p),
                              })
                            },
                          })
                        }
                        openProfile={() => openProfile(row.userId)}
                      />
                    )),
                )}

              {((hasNextPage && !isFetchingNextPage) || isFetchingNextPage) && (
                <li>
                  {hasNextPage && !isFetchingNextPage && (
                    <Button label="Load More" onClick={() => fetchNextPage()} />
                  )}

                  {isFetchingNextPage && (
                    <div className="leaderboard--infinite-spinner">
                      <Spinner size={3} />
                    </div>
                  )}
                </li>
              )}

              {showUserRow() && (
                <LeaderboardRow
                  isMe
                  showRank
                  row={myRow.row}
                  results={data.pages[0].value.totalCount}
                  comments={myRow.row.commentCount.toString()}
                  likes={myRow.row.likeCount.toString()}
                  toggleLike={() =>
                    toggleLike(myRow.row.id, track, {
                      onSuccess: () =>
                        refetch({
                          refetchPage: p => getPageNumber(p) === '1',
                        }),
                    })
                  }
                  score={formattedScore(
                    myRow.row.score,
                    data.pages[0].value.scoring,
                  )}
                  key={`${myRow.row.name}-${myRow.row.rank}`}
                  openComments={() =>
                    openComments({
                      ...myRow.row,
                      track,
                      training: { workout: workoutData },
                      onCommentSuccess: () => {
                        refetch({
                          refetchPage: p => getPageNumber(p) === '1',
                        })
                      },
                    })
                  }
                  openProfile={() => openProfile(myRow.row.userId)}
                />
              )}
            </ul>
          </>
        )}
      </section>
    </AppLayout>
  )
}

export default WorkshopLeaderboard
